import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import {
  FiCheck,
  FiLoader,
  FiArrowRight
} from 'react-icons/fi';

const formatPrice = (value, currency) => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '—';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2
    }).format(numericValue);
  } catch {
    return numericValue.toFixed(2);
  }
};

export default function BillingPlanCard({
  plan,
  billingInterval,
  currency,
  isCurrent,
  canManage,
  loading = false,
  checkoutInProgress = false,
  onChoose
}) {
  const price =
    billingInterval === 'year'
      ? plan?.yearly_price
      : plan?.monthly_price;

  const isFree = Boolean(plan?.is_free);

  /*
   * This plan is the one currently being submitted.
   */
  const isSelectedLoading = Boolean(loading);

  /*
   * Another checkout is already in progress.
   *
   * This disables the other plans without making them
   * display a loading state.
   */
  const isAnotherCheckoutInProgress =
    checkoutInProgress && !isSelectedLoading;

  /*
   * Determine whether this button can be clicked.
   */
  const isDisabled =
    isCurrent ||
    isFree ||
    !canManage ||
    isAnotherCheckoutInProgress ||
    isSelectedLoading;

  /*
   * Button label.
   *
   * IMPORTANT:
   * Only the selected plan gets the loading text.
   */
  const buttonLabel = isCurrent
    ? 'Current Plan'
    : isFree
      ? 'Free Plan'
      : isSelectedLoading
        ? 'Starting checkout...'
        : `Choose ${plan?.name || 'Plan'}`;

  return (
    <article
      className={`group relative flex min-h-[330px] flex-col overflow-hidden rounded-[28px] border p-6 transition-all duration-300 ${
        isCurrent
          ? 'border-blue-200 bg-gradient-to-br from-blue-50/80 via-white to-white shadow-md shadow-blue-100'
          : 'border-slate-200 bg-white hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/60'
      }`}
    >
      {/* Subtle top accent */}
      <div
        className={`absolute inset-x-0 top-0 h-1 ${
          isCurrent
            ? 'bg-blue-500'
            : 'bg-slate-100 transition-colors group-hover:bg-slate-300'
        }`}
      />

      {/* Current badge */}
      {isCurrent && (
        <span className="absolute right-5 top-5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-blue-700">
          Current
        </span>
      )}

      {/* Plan name */}
      <div className="mb-6 pr-20">
        <h3 className="text-xl font-black tracking-tight text-slate-950">
          {plan?.name || 'Plan'}
        </h3>

        <p className="mt-2 min-h-[48px] text-sm leading-6 text-slate-500">
          {plan?.description ||
            'Flexible tools for your accounting workflow.'}
        </p>
      </div>

      {/* Price */}
      <div className="mb-7">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-black tracking-tight text-slate-950">
            {isFree
              ? 'Free'
              : formatPrice(
                  price,
                  currency
                )}
          </span>

          {!isFree && (
            <span className="text-sm font-semibold text-slate-400">
              /{' '}
              {billingInterval === 'year'
                ? 'year'
                : 'month'}
            </span>
          )}
        </div>

        {!isFree &&
          billingInterval === 'year' && (
            <p className="mt-1 text-xs font-semibold text-slate-400">
              Billed annually
            </p>
          )}
      </div>

      {/* CTA */}
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => {
          if (
            !isDisabled &&
            onChoose
          ) {
            onChoose(plan);
          }
        }}
        className={`mt-auto flex min-h-[48px] items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition-all ${
          isCurrent
            ? 'cursor-default bg-slate-100 text-slate-400'
            : isFree || !canManage
              ? 'cursor-not-allowed bg-slate-100 text-slate-400'
              : isSelectedLoading
                ? 'cursor-wait bg-slate-900 text-white'
                : isAnotherCheckoutInProgress
                  ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                  : 'bg-slate-950 text-white shadow-lg shadow-slate-200 hover:bg-slate-800 hover:shadow-xl'
        }`}
      >
        {isSelectedLoading ? (
          <>
            <SafeIcon
              icon={FiLoader}
              className="animate-spin"
            />

            Starting checkout...
          </>
        ) : (
          <>
            {buttonLabel}

            {!isCurrent &&
              !isFree &&
              canManage &&
              !isAnotherCheckoutInProgress && (
                <SafeIcon
                  icon={FiArrowRight}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              )}
          </>
        )}
      </button>

      {/* Stripe security */}
      {!isFree && (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs font-semibold text-slate-400">
          <SafeIcon
            icon={FiCheck}
            className="text-emerald-500"
          />

          Secure Stripe Checkout
        </div>
      )}
    </article>
  );
}